import Savings from "@mui/icons-material/Savings";
import Security from "@mui/icons-material/Security";
import Settings from "@mui/icons-material/Settings";
import Storage from "@mui/icons-material/Storage";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { ReactElement } from "react";
import pageData from "../../data/pages/solutions-intelligent-logstream.json";
import { AccordionSection } from "../../components/AccordionSection/AccordionSection";
import { CardsSection } from "../../components/CardsSection";
import type { MdaiCardProps } from "../../components/MdaiCard";
import { MdaiTable } from "../../components/MdaiTable";
import { PageTemplate } from "../../components/PageTemplate";
import { TextGroup } from "../../components/TextGroup";
import type { SolutionDetailPageTemplate } from "../../types/solutionDetailPageTemplate";
import { resolveCdnAssetUrl } from "../../utils/cdnAssetUrl";

const typedPageData = pageData as SolutionDetailPageTemplate;
const CARD_ICONS = [Savings, Security, Settings, Storage] as const;

const ILSCards: MdaiCardProps[] = (typedPageData.cards ?? []).map((card, index) => ({
  Icon: CARD_ICONS[index] ?? Savings,
  title: card.title,
  body: card.body,
}));

const complianceSection = typedPageData.cardsSections?.find((s) => s.id === "compliance");

export default function IntelligentLogStream(): ReactElement {
  return (
    <Box>
      <PageTemplate
        id="intelligent-logstream"
        hero={{
          title: typedPageData.hero.title,
          subtitle: typedPageData.hero.subtitle,
          image: typedPageData.hero.image
            ? {
                src: resolveCdnAssetUrl(typedPageData.hero.image.src),
                alt: typedPageData.hero.image.alt,
              }
            : undefined,
          buttons: typedPageData.hero.button
            ? [{ text: typedPageData.hero.button.text, href: typedPageData.hero.button.href }]
            : [],
        }}
        topTabs={{
          headline: typedPageData.topTabs.headline,
          subhead: typedPageData.topTabs.subhead,
          tabs: (typedPageData.topTabs.tabs ?? []).map((tab) => ({
            src: resolveCdnAssetUrl(tab.src),
            title: tab.title,
            type: tab.type ?? "image",
            alt: tab.alt,
          })),
          caption: (
            <>
              {typedPageData.topTabs.caption
                .split("\n\n")
                .map((paragraph, index) => (
                  <Typography
                    key={`intelligent-logstream-caption-${index.toString()}`}
                    variant="subtitle2"
                    mb={4}
                  >
                    {paragraph}
                  </Typography>
                ))}
            </>
          ),
        }}
        sections={
          <>
            {typedPageData.textGroups?.[0] && (
              <TextGroup
                id="intelligent-logstream-tg1"
                title={typedPageData.textGroups[0].title}
                subtitle={typedPageData.textGroups[0].subtitle}
                bodies={typedPageData.textGroups[0].bodies}
                bullets={typedPageData.textGroups[0].bullets}
              />
            )}
            <AccordionSection
              title={typedPageData.accordionSection?.title}
              overline={typedPageData.accordionSection?.overline}
              description={typedPageData.accordionSection?.description}
              items={typedPageData.accordionSection?.items}
            />

            {typedPageData.textGroups?.[1] && (
              <TextGroup
                id="intelligent-logstream-tg2"
                title={typedPageData.textGroups[1].title}
                subtitle={typedPageData.textGroups[1].subtitle}
                bodies={typedPageData.textGroups[1].bodies}
                bullets={typedPageData.textGroups[1].bullets}
              />
            )}

            {typedPageData.table && (
              <MdaiTable
                id="intelligent-logstream"
                tableTitle={typedPageData.table.title}
                headers={typedPageData.table.headers}
                rows={typedPageData.table.rows}
              />
            )}

            {ILSCards.length > 0 && (
              <CardsSection
                title={complianceSection?.title}
                cards={ILSCards}
              />
            )}
          </>
        }
        bottomButton={typedPageData.bottomButton}
      />
    </Box>
  );
}
